import { css, type CSSObject } from '@emotion/react'
import {
  Color,
  Shadow,
  ShadowElevation,
  Size,
  Spacing,
  Theme,
  ThemedColors,
  Typography,
  ZIndex,
} from '@opallabs/gem'

type CommonArgs = {
  hasError?: boolean
  hasTokenStrip?: boolean
  isOpen?: boolean
  optionCount?: number
  theme: Theme
}

const rootSelector = 'multi-check-select' as const

export const state = {
  hasSelection: 'has-selection',
} as const

export const selectors = {
  root: rootSelector,
  persistentOption: `${rootSelector}__persistent-option`,
  input: `${rootSelector}-input`,
  inputPlaceholder: 'multi-select-value-input-placeholder',
} as const

export const inputWrapper: CSSObject = {
  position: 'relative',
  'label + .multi-select-value-input': {
    marginTop: Spacing.XS,
  },
}

// The design's label style, the same one `InputLabel` wears.
export const inputLabel = (theme: Theme): CSSObject => ({
  color: Color[theme].Text.Default,
  ...Typography.H50,
})

export const inputContainer = ({
  hasError,
  isOpen,
  theme,
}: CommonArgs): CSSObject => ({
  alignItems: 'center',
  border: 'solid 1px',
  borderRadius: Size.Rounding,
  boxSizing: 'border-box',
  display: 'flex',
  flexFlow: 'row nowrap',
  height: Spacing.XS * 9,
  padding: Spacing.S,
  cursor: 'pointer',
  backgroundColor: Color[theme].Surface.N100,
  borderColor: isOpen
    ? Color[theme].Surface.A200
    : hasError
      ? Color[theme].Surface.R100
      : Color[theme].Surface.N300,
  '&:disabled': {
    cursor: 'not-allowed',
    opacity: 0.7,
    backgroundColor: Color[theme].Surface.N200,
  },
  '&:focused': {
    borderColor: Color[theme].Surface.A200,
  },
})

export const placeholder = (): CSSObject => ({
  ...Typography.T500,
  color: `var(${ThemedColors.TextSecondary})`,
})

export const selection = (theme: Theme): CSSObject => ({
  flex: 1,
  overflow: 'hidden',
  textAlign: 'left',
  whiteSpace: 'nowrap',
  ...Typography.T500,
  color: Color[theme].Text.Default,
})

export const selectionList: CSSObject = {
  margin: 0,
  padding: 0,
  listStyleType: 'none',
  display: 'flex',
  overflow: 'hidden',
  columnGap: `${Spacing.XS}px`,
  fontSize: '12px',
  fontWeight: 600,

  li: {
    whiteSpace: 'nowrap',
    maxWidth: '100px',
  },

  'li span': {
    display: 'inline block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',

    '&[aria-invalid="true"]': {
      opacity: 0.5,
    },
  },
}

export const menu = ({
  hasTokenStrip,
  theme,
  optionCount,
}: CommonArgs): CSSObject => ({
  borderBottomLeftRadius: Size.Rounding,
  borderBottomRightRadius: Size.Rounding,
  boxSizing: 'border-box',
  display: 'flex',
  flexFlow: 'column nowrap',
  listStyle: 'none',
  marginBlock: 0,
  // The token strip wraps above the list, so the taller cap keeps room for
  // both it and the footer.
  maxHeight:
    optionCount && optionCount > 4 ? (hasTokenStrip ? 320 : 180) : 'unset',
  padding: 0,
  position: 'fixed',
  width: 'max-content',
  top: 0,
  left: 0,
  zIndex: ZIndex.Shroud.Popover,
  backgroundColor: Color[theme].Surface.N000,
  ...Shadow[ShadowElevation.E3],

  // Ensures menu is still interactive when Radix focus trapping is active
  pointerEvents: 'auto',
})

// Workaround for Blink rendering bug
// `overflow` on absolutely-positioned elements with `max-height`
// causes children to be rendered blurry
export const menuContent: CSSObject = {
  height: '100%',
  overflowY: 'auto',
  display: 'flex',
  flexFlow: 'column nowrap',
  rowGap: Spacing.XS,
  padding: `${Spacing.S}px ${Spacing.XS}px ${Spacing.XS}px`,
  margin: 0,
}

// Sized like a `MenuOption` row (20px line, 6px 8px padding, 1px border) so
// swapping between one match and no match does not move the menu's height.
export const noOptions: CSSObject = {
  ...Typography.T500,
  border: 'solid 1px transparent',
  boxSizing: 'border-box',
  lineHeight: '20px',
  padding: '6px 8px',
  textAlign: 'center',
}

export const searchContainer = ({ theme }: CommonArgs): CSSObject => ({
  padding: `${Spacing.S}px ${Spacing.XS}px 0`,
  backgroundColor: Color[theme].Surface.N000,

  '+ ul[role="listbox"]': {
    paddingTop: `${Spacing.XS}px`,
  },
})

export const persistentOption = (
  theme: Theme,
  isHighlighted: boolean
): CSSObject => ({
  ...Typography.T500,
  padding: '6px 8px',
  backgroundColor: isHighlighted
    ? Color[theme].Translucent.A08
    : Color[theme].Surface.N000,
  '&:hover': {
    cursor: 'pointer',
    backgroundColor: Color[theme].Translucent.A08,
  },
})

export const multiUserSelect = css({
  [`.${selectors.input}`]: {
    columnGap: Spacing.XS,
    justifyContent: 'space-between',
  },
})

// Minimum width for the multi-user menu to accommodate the custom footer
export const multiUserMenuMinWidth = 300

export const customFooter = (theme: Theme): CSSObject => ({
  // The menu container is a flex column with a max-height. Without this the
  // scrolling option list would squeeze the footer to nothing.
  flexShrink: 0,
  borderTop: `1px solid ${Color[theme].Surface.N300}`,
  padding: `${Spacing.XS}px ${Spacing.S}px`,
})
